import { IconCheck } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import Select, { StylesConfig, components, MultiValueProps } from "react-select";
import useIndexAgent from "../../../../services/agent/hooks/useIndexAgent";

interface OptionType {
    value: string;
    label: string;
}

interface AgentSelectFilterComponentProps {
    onChange: (agentFilter: string[]) => void;
    defaultValue: string | undefined;
}

const AgentSelectFilterComponent: React.FC<AgentSelectFilterComponentProps> = ({ onChange, defaultValue }) => {
    const defaultAgent = { value: "0", label: "All Agent" };
    const [agents, setAgents] = useState<OptionType[]>([defaultAgent]);
    const [selectedAgent, setSelectedAgent] = useState<OptionType[]>([defaultAgent]);

    useEffect(() => {
        if (defaultValue === '') {
            console.log('default value is empty');
            setSelectedAgent([defaultAgent]);
        }
    }, [defaultValue]);

    const { data: agentData, isSuccess, isError, isFetching } = useIndexAgent({
        filters: {
            all: 1,
        }
    });

    useEffect(() => {
        if (agentData) {
            setAgents([
                defaultAgent,
                ...agentData.data.map((agent) => ({
                    value: agent.id,
                    label: agent.name,
                })),
            ]);
        }
    }, [agentData]);

    useEffect(() => {
        if (agentData && selectedAgent.length === 1 && selectedAgent[0].value === defaultAgent.value) {
            onChange([]);
        }
    }, [agentData, selectedAgent]);

    return (
        <AgentSelect
            defaultAgent={defaultAgent}
            agents={agents}
            isFetching={isFetching}
            isSuccess={isSuccess}
            isError={isError}
            selectedAgent={selectedAgent}
            setSelectedAgent={setSelectedAgent}
            setAgentFilter={onChange}
        />
    );
};

export default AgentSelectFilterComponent;

const customStyles: StylesConfig<OptionType, boolean> = {
    menuPortal: (base) => ({
        ...base,
        zIndex: 1000, // Pastikan z-index cukup tinggi
    }),
};

interface AgentSelectProps {
    defaultAgent: OptionType;
    agents: OptionType[];
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    selectedAgent: OptionType[];
    setSelectedAgent: (agents: OptionType[]) => void;
    setAgentFilter?: (agentFilter: string[]) => void;
}

const CustomOption = (props: any) => {
    const { data, isSelected, innerRef, innerProps } = props;

    return (
        <div ref={innerRef} {...innerProps} className="d-flex justify-content-between align-items-center p-2">
            <span>{data.label}</span>
            {isSelected && <IconCheck size={16} />}
        </div>
    );
};

const AgentSelect: React.FC<AgentSelectProps> = ({
    defaultAgent,
    agents,
    isFetching,
    isSuccess,
    isError,
    selectedAgent,
    setSelectedAgent,
    setAgentFilter,
}) => {
    const handleChange = (selected: OptionType[]) => {
        if (selected.length === 1 && selected[0].value === defaultAgent.value) {
            setSelectedAgent([defaultAgent]);
            setAgentFilter?.([]);
        } else {
            setSelectedAgent(selected.filter((agent) => agent.value !== defaultAgent.value));
            setAgentFilter?.(selected.filter((agent) => agent.value !== defaultAgent.value).map((agent) => agent.value));
        }
    };

    return (
        <Select
            value={selectedAgent}
            options={agents}
            className="p-0"
            styles={customStyles}
            menuPortalTarget={document.body}
            menuPosition="absolute"
            isLoading={isFetching}
            isDisabled={!isSuccess || isError}
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            onChange={(selected) => handleChange(selected as OptionType[])}
            components={{
                Option: CustomOption,
                MultiValueContainer: ({ children, ...props }) => {
                    const selectedValues = props.selectProps.value as OptionType[];
                    const index = selectedValues.findIndex((v) => v.value === props.data.value);
                    const totalSelected = selectedValues.length;
                    const maxVisible = 3; // Maksimal item yang ditampilkan sebelum "X More"

                    if (index < maxVisible) {
                        return children;
                    }

                    if (index === maxVisible) {
                        const remainingCount = totalSelected - maxVisible;
                        return (
                            <div className="d-flex align-items-center px-2 bg-light rounded">
                                <span>+{remainingCount} More</span>
                            </div>
                        );
                    }

                    return null;
                },
                MultiValue: (props: MultiValueProps<any>) => {
                    const selectedValues = props.selectProps.value as OptionType[];
                    const index = selectedValues.findIndex((v) => v.value === props.data.value);
                    const maxVisible = 3;

                    if (index > maxVisible) return null;

                    return <components.MultiValue {...props} />;
                },
                ValueContainer: ({ getValue }) => {
                    const selected = getValue();
                    return (
                        <div className="p-2">
                            {selected.length === 1 && selected[0].value === defaultAgent.value
                                ? defaultAgent.label
                                : `${selected.length} Agents Selected`}
                        </div>
                    );
                },
            }}
        />
    );
};